import React from "react";
import BaseSearchPage from "../baseSearchPage";
import HotelOnlySearchResultItem from "./hotelOnlySearchResultItem";
import { useApi } from "/src/helpers/Hooks/useApi";
import { addDays, differenceInDays, parse, format } from "date-fns";

export default function HotelOnlySearchPage() {

    const apiClient = useApi();
    const fetcher = async (searchParams, filterParams, minRank, recommendedExhausted) => {
        const apiSearchParams = new URLSearchParams();
        apiSearchParams.append("to", searchParams.to);
        apiSearchParams.append("durations", searchParams.duration);
        apiSearchParams.append("earlyStart", searchParams.earlyStart);

        // The search widget builds /AO/ urls as checkin/checkout, so when the second date is
        // exactly checkin + nights it is a fixed-date search; anything else is treated as a
        // flexible start-date range (matching package deeplinks).
        const duration = parseInt(searchParams.duration);
        const start = parse(searchParams.earlyStart, "yyyy-MM-dd", new Date());
        const end = parse(searchParams.lateStart, "yyyy-MM-dd", new Date());
        const isCheckoutDate = differenceInDays(end, start) == duration;
        apiSearchParams.append("lateStart", isCheckoutDate ? searchParams.earlyStart : searchParams.lateStart);

        apiSearchParams.append("rooms", searchParams.rooms);
        if (filterParams["rating"]) {
            apiSearchParams.append("fRatings", filterParams["rating"]);
        }
        if (filterParams["trip-advisor"]) {
            apiSearchParams.append("fMinTripAdvisor", filterParams["trip-advisor"]);
        }

        if (filterParams.facilities) {
            apiSearchParams.append("fFacilities", filterParams.facilities);
        }
        if (filterParams["board-basis"]) {
            apiSearchParams.append("fBoardBases", filterParams["board-basis"]);
        }
        // Merged Destination facet (mixed country/region/resort codes). The legacy
        // per-level params below still work for old shared URLs.
        if (filterParams["destinations"]) {
            apiSearchParams.append("fDestinations", filterParams["destinations"]);
        }
        if (filterParams["countries"]) {
            apiSearchParams.append("fCountries", filterParams["countries"]);
        }
        if (filterParams["regions"]) {
            apiSearchParams.append("fRegions", filterParams["regions"]);
        }
        if (filterParams["resorts"]) {
            apiSearchParams.append("fResorts", filterParams["resorts"]);
        }

        if (filterParams.sort) {
            apiSearchParams.append("sort", filterParams.sort);
        }
        if (minRank) {
            apiSearchParams.append("minRank", minRank);
        }
        if (recommendedExhausted) {
            apiSearchParams.append("recommendedExhausted", "true");
        }
        const postsData = await apiClient.doHotelOnlySearch(apiSearchParams);

        return postsData;
    }

    const searchFormModelBuilder = (searchParams) => {

        const start = parse(searchParams.earlyStart, "yyyy-MM-dd", new Date());
        // The URL's second date is only a checkout date on widget-built searches; on
        // flexible/window deeplinks it's the start-date window end (possibly == start,
        // which read as 0 nights). The form wants checkin/checkout, and checkin +
        // duration is correct for both shapes.
        const end = addDays(start, parseInt(searchParams.duration));

        return {
            dep: ["stay-only"],
            to: { code: searchParams.to, name: "unknwon" },
            startDate: format(start, "yyyy-MM-dd"),
            endDate: format(end, "yyyy-MM-dd"),
            pax: searchParams.rooms,
            // Without this the search widget defaults to "packages" and the
            // form flips back to the Holidays tab after a hotel-only search.
            type: "stays"
        };
    }

    return <BaseSearchPage
        resultFetcher={fetcher}
        searchFormBuilder={searchFormModelBuilder}
        resultTemplate={(x) => (<HotelOnlySearchResultItem result={x} />)}
        listName="Hotel Only Results"
    ></BaseSearchPage>
}
